/* @flow */

import React from 'react';
import {connect} from 'react-redux';

import AmountFormatterComponent from 'components/formatters/amount';
import LoadingComponent from 'components/loading';
import NumberFormatterComponent from 'components/formatters/number';
import PercentageFormatterComponent from 'components/formatters/percentage';

import BuyerInvestmentProfileFormViewComponent from '../form/view';

import InvestmentProfileDefaults from 'models/buyers/investmentProfiles/investmentProfileDefaults';
import InvestmentProfileValues from 'models/buyers/investmentProfiles/investmentProfileValues';

import InvestmentProfileForm from '../form/model';

import BuyerService from 'services/BuyerService';
import {handleApiFormResponse, handleFormChange, handleFormValidation} from 'lib/forms';
import loadConstraints from 'lib/validation/loadConstraints';
import NotificationHelper from 'lib/notifications';

type BuyerInvestmentProfileFormComponentProps = {
  activeCompanyId: number;
  activeCompanyInvestmentProfileId: number;
  defaults: InvestmentProfileDefaults;
  isActive: boolean;
  profile: InvestmentProfileValues;
  hasInvoicesListedTo: boolean;
  canExecute: boolean;
  onUpdateSucess: () => void;
}

type BuyerInvestmentProfileFormComponentState = {
  constraints: any;
  errors: Map<string, ?string>;
  form: InvestmentProfileForm;
  formErrors: string[];
  isLoading: boolean;
  isProcessing: boolean;
  placeholders: any;
}

class BuyerInvestmentProfileFormComponent extends React.Component<BuyerInvestmentProfileFormComponentProps, BuyerInvestmentProfileFormComponentState> {

  constructor(props) {
    super(props);

    this.state = {
      constraints: null,
      errors: null,
      form: null,
      formErrors: [],
      isLoading: true,
      isProcessing: false,
      placeholders: null
    };
  }

  componentDidMount = async () => {
    try {
      // Load & build constraints
      const constraints = await loadConstraints('InvestmentProfile');
      constraints.amount.numericality.greaterThanOrEqualTo = this.props.defaults.amountMinValue;
      constraints.minInvoiceAmount.numericality.greaterThanOrEqualTo = this.props.defaults.minAmountMinValue;
      constraints.maxInvoiceAmount.numericality.greaterThanOrEqualTo = this.props.defaults.maxAmountMinValue;
      constraints.maxInvoiceAmount.numericality.lessThanOrEqualTo = this.props.defaults.maxAmountMaxValue;

      const placeholders = {
        amount: <AmountFormatterComponent amount={this.props.defaults.amountMinValue} />,
        minAmount: <AmountFormatterComponent amount={this.props.defaults.minAmountMinValue} />,
        maxAmount: <AmountFormatterComponent amount={this.props.defaults.maxAmountMinValue} />,
        multiplier: <PercentageFormatterComponent noFractionDigits value={(this.props.defaults.maxAmountMultiplier * 100)} />
      };

      this.setState({
        constraints,
        errors: new Map(Object.keys(constraints).map(e => [e, undefined])),
        form: new InvestmentProfileForm(this.props.profile),
        isLoading: false,
        placeholders
      });
    } catch (e) {
      console.error(e);
      NotificationHelper.createNotification(NotificationHelper.TYPE_ERROR);
    }
  };

  handleChange = (fieldName: string) => (event: object) => handleFormChange.call(this, fieldName, event.target.value);
  handleChangeBankAccount = (bankAccountId: number) => handleFormChange.call(this, 'bankAccountId', bankAccountId);

  validateForm = () => handleFormValidation.call(this);

  submitNameForm = async () => {
    if (this.state.form.nameUpdate)
    {
      this.setState({formErrors: [], isProcessing: true});
      try {
        const request = this.state.form.toUpdateNameRequest(this.props.activeCompanyId, this.props.activeCompanyInvestmentProfileId);
        await BuyerService.updateInvestmentProfileName(request);
        NotificationHelper.createNotification(NotificationHelper.TYPE_SUCCESS);
        this.props.onUpdateSucess();
      } catch (e) {
        handleApiFormResponse.call(this, e);
      }
    }
  }

  submitForm = async () => {
    if (!this.validateForm()) return;
    this.setState({formErrors: [], isProcessing: true});

    try {
      const request = this.state.form.toUpdateRequest(this.props.activeCompanyId, this.props.activeCompanyInvestmentProfileId);
      await BuyerService.updateInvestmentProfile(request);
      NotificationHelper.createNotification(NotificationHelper.TYPE_SUCCESS);
      this.props.onUpdateSucess();
    } catch (e) {
      handleApiFormResponse.call(this, e);
    }
  };

  render() {
    if (this.state.isLoading) {
      return (<LoadingComponent />);
    }

    return (
      <BuyerInvestmentProfileFormViewComponent
        errors={this.state.errors}
        form={this.state.form}
        formErrors={this.state.formErrors}
        isActive={this.props.isActive}
        isProcessing={this.state.isProcessing}
        placeholders={this.state.placeholders}
        isModifyForm={true}
        btnId={"general.btn.update"}
        updateNameBtnId={'buyer.ip.updateipnamebtn'}
        pageTitle={`buyer.ip.requested${this.props.isActive ? '' : '.disabled'}`}
        hasInvoicesListedTo={this.props.hasInvoicesListedTo}
        canExecute={this.props.canExecute}
        handleChange={this.handleChange}
        handleChangeBankAccount={this.handleChangeBankAccount}
        submitForm={this.submitForm}
        submitNameForm={this.submitNameForm}
      />
    );
  }
}

const mapStateToProps = state => ({
  activeCompanyId: state.auth.user.activeCompany.id,
  activeCompanyInvestmentProfileId: state.auth.user.activeCompany.investmentProfile.id
});

export default connect(mapStateToProps)(BuyerInvestmentProfileFormComponent);
